// components/Home/EventsMap/index.jsx
import { useEffect, useRef, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import mapboxgl from 'mapbox-gl'
import { useTranslation } from 'react-i18next'
import TranslationNS from '@/utils/Constants/TranslationNS'
import rsnatchIcon from '@/images/scoring_icon.svg'

import 'mapbox-gl/dist/mapbox-gl.css'
import ReactCountryFlag from 'react-country-flag'
import COUNTRIES_EN from 'world_countries_lists/data/countries/en/world.json'

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN

const DEFAULT_CENTER = [-7.9811, 31.6295] // Marrakesh coordinates as default center
const DEFAULT_ZOOM = 1.2
const SOURCE_ID = 'events-source'

const COUNTRY_CODE_BY_NAME = new Map(COUNTRIES_EN.map((c) => [c.name, c.alpha2]))

function countryToFlag(countryName) {
    const code = COUNTRY_CODE_BY_NAME.get(countryName)
    if (code) {
        return <ReactCountryFlag countryCode={code} svg style={{ width: '1em', height: '1em' }} title={code} />
    }
    return '🌍'
}

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
function escapeHtml(s) {
    return String(s ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch])
}

function countryToFlagHTML(countryName) {
    const result = countryToFlag(countryName)
    if (typeof result === 'string') return result
    return renderToStaticMarkup(result)
}

function formatDivisionType(divisionType) {
    if (divisionType === 'both') return '#TEAM - #INDIV'
    if (divisionType === 'team') return '#TEAM'
    if (divisionType === 'individual') return '#INDIV'
    return ''
}

function buildMarkerEl(name, logoUrl) {
    const wrap = document.createElement('div')
    wrap.className = 'avatar-marker'
    wrap.style.cursor = 'pointer'

    const img = document.createElement('img')
    img.alt = name || 'event'
    img.width = 48
    img.height = 48
    img.decoding = 'async'
    img.onerror = () => {
        img.onerror = null
        img.src = rsnatchIcon
    }
    img.src = logoUrl
    img.style.width = '48px'
    img.style.height = '48px'
    img.style.borderRadius = '50%'
    img.style.objectFit = 'cover'
    img.style.background = 'white'
    wrap.appendChild(img)

    const label = document.createElement('div')
    label.className = 'city-marker'
    label.textContent = ''
    // label.textContent = name || ''
    wrap.appendChild(label)
    return wrap
}

export default function EventsMap({
    events = [],
    initialCenter = DEFAULT_CENTER,
    initialZoom = DEFAULT_ZOOM,
    autoFit = false,
    className = '',
    style = {},
}) {
    const { t } = useTranslation(TranslationNS.HOME_PAGE)
    const { t: tMonths } = useTranslation(TranslationNS.MONTHS)
    const mapRef = useRef(null)
    const containerRef = useRef(null)
    const [ready, setReady] = useState(false)
    const markersRef = useRef({})
    const fittedOnceRef = useRef(false)
    const eventsRef = useRef([])
    const tRef = useRef(t)
    tRef.current = t

    // init map once
    useEffect(() => {
        mapboxgl.accessToken = MAPBOX_TOKEN
        if (mapRef.current || !containerRef.current) return

        const map = new mapboxgl.Map({
            container: containerRef.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: initialCenter,
            zoom: initialZoom,
            projection: 'mercator',
        })

        function syncMarkers() {
            if (!map.isSourceLoaded(SOURCE_ID)) return

            const features = map.querySourceFeatures(SOURCE_ID, {
                filter: ['!', ['has', 'point_count']],
            })

            const seen = new Set()
            features.forEach((f) => {
                const id = f.properties.id
                seen.add(id)

                const cached = markersRef.current[id]
                if (cached) {
                    const [lng, lat] = f.geometry.coordinates
                    cached.setLngLat([lng, lat])
                    if (!cached._attached) {
                        cached.addTo(map)
                        cached._attached = true
                    }
                    return
                }

                const n = eventsRef.current.find((e) => e.id === id)
                if (!n) return

                const popup = new mapboxgl.Popup({
                    offset: 28,
                    maxWidth: 360,
                    className: 'event-popup',
                })
                let popupBuilt = false
                popup.on('open', () => {
                    if (popupBuilt) return
                    popupBuilt = true
                    popup.setHTML(`

                    <div
                    style="
                        width: 100%;
                        height: 100%;
                        font-family: sans-serif;
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                    "
                    >
                    <img
                        src="${escapeHtml(n.thumbnailUrl)}"
                        alt="${escapeHtml(n.name)}"
                        decoding="async"
                        style="
                        width: 72px;
                        height: 72px;
                        object-fit: cover;
                        border-radius: 12px;
                        border: 1.5px solid #000000;
                        display: block;
                        margin-bottom: 24px;
                        "
                    />
                    <div style="flex: 1">
                        <div
                        style="
                            font-size: 18px;
                            font-weight: 700;
                            text-transform: uppercase;
                            line-height: 1.3;
                            margin-bottom: 6px;
                        "
                        >
                        ${escapeHtml(n.name)}
                        </div>
                        <div
                        style="
                            font-size: 14px;
                            color: #555;
                            display: flex;
                            align-items: center;
                            gap: 0.5rem;
                        "
                        >
                        ${countryToFlagHTML(n.country)} ${escapeHtml(n.locationText)}${
                        n.category ? `&nbsp;·&nbsp;${escapeHtml(n.category)}` : ''
                    }
                        </div>
                        <div style="font-size: 14px; color: #acacac; margin-bottom: 14px">
                        ${formatDivisionType(n.divisionType)}
                        </div>
                    </div>
                    <div
                        style="
                        padding-top: 10px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        "
                    >
                        <div style="font-size: 16px; font-weight: 600; color: #333">
                        ${formatDateRange(n.startDate, n.endDate)}
                        </div>

                        <a
                        href="/${encodeURIComponent(n.eventNumber ?? '')}"
                        target="_blank"
                        style="
                            padding: 7px 18px;
                            border: 1.5px solid #ccc;
                            border-radius: 8px;
                            font-size: 13px;
                            font-weight: 600;
                            text-decoration: none;
                            color: #222;
                            white-space: nowrap;
                        "
                        >
                        ${escapeHtml(tRef.current('View Details'))}
                        </a>
                    </div>
                    </div>


                `)
                })
                const marker = new mapboxgl.Marker({
                    element: buildMarkerEl(n.name, n.iconUrl),
                    anchor: 'bottom',
                    offset: [0, -6],
                })
                    .setLngLat([n.longitude, n.latitude])
                    .setPopup(popup)
                    .addTo(map)
                marker._attached = true
                markersRef.current[id] = marker
            })

            Object.keys(markersRef.current).forEach((id) => {
                if (seen.has(id)) return
                const m = markersRef.current[id]
                if (m._attached) {
                    m.remove()
                    m._attached = false
                }
            })
        }

        map.on('load', () => {
            if (!document.getElementById('event-popup-styles')) {
                const style = document.createElement('style')
                style.id = 'event-popup-styles'
                style.textContent = `
                    .event-popup .mapboxgl-popup-content {
                        border-radius: 16px;
                        border: 1.5px solid #e5e7eb;
                        padding: 16px;
                        box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                        min-width: 360px !important;
                        min-height: 260px !important;
                        box-sizing: border-box !important;
                        overflow: hidden;
                    }
                    .event-popup .mapboxgl-popup-close-button {
                        top: 10px;
                        right: 10px;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        border: 1.5px solid #e5e7eb;
                        background: #fff;
                        font-size: 20px;
                        color: #555;
                        line-height: 26px;
                        text-align: center;
                        padding: 0;
                    }
                    .event-popup .mapboxgl-popup-close-button:hover {
                        background: #f5f5f5;
                        color: #111;
                    }
                `
                document.head.appendChild(style)
            }

            map.addSource(SOURCE_ID, {
                type: 'geojson',
                data: { type: 'FeatureCollection', features: [] },
                cluster: true,
                clusterMaxZoom: 12,
                clusterRadius: 50,
            })

            map.addLayer({
                id: 'clusters',
                type: 'circle',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                paint: {
                    'circle-color': [
                        'step',
                        ['get', 'point_count'],
                        'rgba(0,0,0,0.5)',
                        10,
                        'rgba(0,0,0,0.5)',
                        30,
                        'rgba(0,0,0,0.5)',
                    ],
                    'circle-radius': ['step', ['get', 'point_count'], 24, 10, 32, 30, 40],
                },
            })

            map.addLayer({
                id: 'cluster-count',
                type: 'symbol',
                source: SOURCE_ID,
                filter: ['has', 'point_count'],
                layout: {
                    'text-field': '{point_count_abbreviated}',
                    'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                    'text-size': 18,
                },
                paint: { 'text-color': '#ffffff' },
            })

            map.on('click', 'clusters', (e) => {
                const [feature] = map.queryRenderedFeatures(e.point, { layers: ['clusters'] })
                map.getSource(SOURCE_ID).getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
                    if (err) return
                    map.easeTo({ center: feature.geometry.coordinates, zoom: zoom + 1 })
                })
            })

            map.on('mouseenter', 'clusters', () => {
                map.getCanvas().style.cursor = 'pointer'
            })
            map.on('mouseleave', 'clusters', () => {
                map.getCanvas().style.cursor = ''
            })

            let syncPending = false
            const scheduleSync = () => {
                if (syncPending) return
                syncPending = true
                requestAnimationFrame(() => {
                    syncPending = false
                    syncMarkers()
                })
            }
            map.on('moveend', scheduleSync)
            map.on('zoomend', scheduleSync)
            map.on('sourcedata', (e) => {
                if (e.sourceId === SOURCE_ID && e.isSourceLoaded) scheduleSync()
            })

            map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right')
            map.addControl(
                new mapboxgl.GeolocateControl({
                    positionOptions: { enableHighAccuracy: true },
                    trackUserLocation: false,
                    showUserLocation: true,
                    showAccuracyCircle: false,
                }),
                'top-right',
            )

            // Worldview overrides:
            //   MA → Western Sahara shown as part of Morocco
            //   PS → Israel/Gaza/West Bank disputed boundaries shown from Palestinian perspective
            const worldviewFilter = [
                'any',
                ['!', ['has', 'worldview']],
                ['match', ['get', 'worldview'], ['all', 'MA', 'PS'], true, false],
            ]
            // Labels to suppress: EH (Western Sahara), IL (merged into Palestine)
            const hiddenCountryLabels = ['EH', 'IL']
            // Hide the IL/PS country-level boundary line (incl. Gaza & West Bank)
            // so Israel and Palestine render as a single landmass labeled "Palestine".
            // Matches both string and boolean variants of `disputed`.
            const mergedBoundaryFilter = [
                '!',
                [
                    'any',
                    ['in', ['get', 'iso_3166_1'], ['literal', ['PS']]],
                    ['in', ['get', 'iso_3166_1_alpha_3'], ['literal', ['PSE']]],
                    [
                        'all',
                        ['in', ['get', 'iso_3166_1'], ['literal', ['IL', 'PS']]],
                        ['any', ['==', ['get', 'disputed'], 'true'], ['==', ['get', 'disputed'], true]],
                    ],
                ],
            ]

            ;(map.getStyle()?.layers ?? []).forEach(({ id, 'source-layer': sourceLayer }) => {
                if (!sourceLayer) return
                // Hide any "disputed boundary" sub-layer outright — that's where
                // Mapbox usually renders the Gaza/West Bank perimeter line.
                if (sourceLayer === 'admin' && /disputed/i.test(id)) {
                    try {
                        map.setLayoutProperty(id, 'visibility', 'none')
                    } catch (_) {}
                    return
                }
                if (!['admin', 'place_label'].includes(sourceLayer)) return
                try {
                    const existing = map.getFilter(id)
                    const baseFilters = existing ? (existing[0] === 'all' ? existing.slice(1) : [existing]) : []
                    const extra =
                        sourceLayer === 'place_label'
                            ? [worldviewFilter, ['!', ['in', ['get', 'iso_3166_1'], ['literal', hiddenCountryLabels]]]]
                            : [worldviewFilter, mergedBoundaryFilter]
                    map.setFilter(id, ['all', ...baseFilters, ...extra])
                } catch (_) {}
            })

            // Custom country-style "Palestine" label over the merged IL/PS landmass.
            // Mapbox streets-v12 may not natively render PS at country prominence,
            // so we add our own symbol layer styled to match other country labels.
            const PALESTINE_LABEL_SOURCE = 'palestine-country-label'
            if (!map.getSource(PALESTINE_LABEL_SOURCE)) {
                map.addSource(PALESTINE_LABEL_SOURCE, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: 'Feature',
                                geometry: { type: 'Point', coordinates: [35.0, 31.5] },
                                properties: { name: 'Palestine' },
                            },
                        ],
                    },
                })
                map.addLayer({
                    id: 'palestine-country-label',
                    type: 'symbol',
                    source: PALESTINE_LABEL_SOURCE,
                    minzoom: 1,
                    maxzoom: 10,
                    layout: {
                        'text-field': ['get', 'name'],
                        'text-font': ['DIN Pro Medium', 'Arial Unicode MS Regular'],
                        'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 4, 13, 6, 16, 9, 20],
                        'text-letter-spacing': 0.05,
                        'text-max-width': 6,
                        'text-padding': 2,
                    },
                    paint: {
                        'text-color': '#000',
                        'text-halo-color': 'rgba(255,255,255,0.85)',
                        'text-halo-width': 1.25,
                    },
                })
            }

            setReady(true)
        })

        mapRef.current = map
        return () => map.remove()
    }, [initialCenter, initialZoom])

    // update GeoJSON source when events change
    useEffect(() => {
        if (!ready || !mapRef.current) return
        const map = mapRef.current

        const normalized = events
            .map((e) => {
                const id = String(e.id ?? `${e?.longitude},${e?.latitude}_${e.name}`)
                const longitude = e?.longitude != null ? parseFloat(e?.longitude) : NaN
                const latitude = e?.latitude != null ? parseFloat(e?.latitude) : NaN
                if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return null
                return {
                    id,
                    name: e.name ?? 'Event',
                    longitude,
                    latitude,
                    iconUrl: e.iconLink?.replace(/(\.[^.]+)$/, '-thumbnail$1') || rsnatchIcon,
                    thumbnailUrl: e.iconLink || rsnatchIcon,
                    category: e?.category,
                    locationText: e?.locationText || '',
                    eventNumber: e?.eventNumber,
                    country: e?.country ?? '',
                    startDate: e?.startDate ?? '',
                    endDate: e?.endDate ?? '',
                    divisionType: e?.divisionType,
                }
            })
            .filter(Boolean)

        // Group events by exact coordinate to detect stacked points
        const coordGroups = {}
        normalized.forEach((n) => {
            const key = `${n.longitude},${n.latitude}`
            if (!coordGroups[key]) coordGroups[key] = []
            coordGroups[key].push(n)
        })

        // Spread stacked events in a small circle so each is individually visible.
        // Store jittered coords on the event so custom HTML markers also use the offset position.
        const JITTER_RADIUS = 0.01 // ~10m, adjust as needed
        const jittered = normalized.map((n) => {
            const key = `${n.longitude},${n.latitude}`
            const group = coordGroups[key]
            const idx = group.indexOf(n)
            if (group.length === 1) return n
            const angle = (2 * Math.PI * idx) / group.length
            return {
                ...n,
                longitude: n.longitude + JITTER_RADIUS * Math.cos(angle),
                latitude: n.latitude + JITTER_RADIUS * Math.sin(angle),
            }
        })

        eventsRef.current = jittered

        map.getSource(SOURCE_ID)?.setData({
            type: 'FeatureCollection',
            features: jittered.map((n) => ({
                type: 'Feature',
                geometry: { type: 'Point', coordinates: [n.longitude, n.latitude] },
                properties: { id: n.id },
            })),
        })

        if (autoFit && normalized.length) {
            const bounds = new mapboxgl.LngLatBounds()
            normalized.forEach((n) => bounds.extend([n.longitude, n.latitude]))
            map.fitBounds(bounds, { padding: 80, duration: fittedOnceRef.current ? 400 : 900 })
            if (normalized.length === 1)
                map.easeTo({ center: [normalized[0].longitude, normalized[0].latitude], zoom: 10, duration: 400 })
            fittedOnceRef.current = true
        }
    }, [events, autoFit, ready])

    const MONTHS = [
        tMonths('January'),
        tMonths('February'),
        tMonths('March'),
        tMonths('April'),
        tMonths('May'),
        tMonths('June'),
        tMonths('July'),
        tMonths('August'),
        tMonths('September'),
        tMonths('October'),
        tMonths('November'),
        tMonths('December'),
    ]

    function formatDateRange(startDate, endDate) {
        if (!startDate) return ''
        const start = new Date(startDate)
        if (isNaN(start.getTime())) return ''

        const startMonth = MONTHS[start.getMonth()]
        const startDay = start.getDate()

        if (!endDate) return `${startMonth} ${startDay}`

        const end = new Date(endDate)
        if (isNaN(end.getTime())) return `${startMonth} ${startDay}`

        const endMonth = MONTHS[end.getMonth()]
        const endDay = end.getDate()

        const sameDay =
            start.getFullYear() === end.getFullYear() &&
            start.getMonth() === end.getMonth() &&
            start.getDate() === end.getDate()

        if (sameDay) return `${startMonth} ${startDay}`

        const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()

        if (sameMonth) return `${startMonth} ${startDay}-${endDay}`

        return `${startMonth} ${startDay} - ${endMonth} ${endDay}`
    }

    return (
        <div
            ref={containerRef}
            className={className}
            style={{ width: '100%', height: '100%', minHeight: '0', ...(style || {}) }}
        />
    )
}
